import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import { Epic, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';
import { actions } from '.';
import { addErrorNotice, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { syncAndAwaitResolve } from '../DataSync';
import { callApiGetSeparations, callApiPutSeparations, callApiRefreshSieves } from './apiCalls';
import { ITempSeparationAction, RemoveSeparationAction, SeparationModel } from './types';

export const initTempSeparations: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.initTempSeparations.toString()),
    mergeMap(() =>
      callApiGetSeparations(state$.value.worksheetEditor.worksheetId).pipe(
        mergeMap(({ Separations: separations }) =>
          of(
            actions.setTempSeparations({
              separations,
            }),
            actions.initTempSeparationsResolve()
          )
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.initTempSeparationsReject(), addErrorNotice('Failed to initialise separations data.', err))
        )
      )
    )
  );

export const validateTempSeparationsOnSeparationChanges: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      actions.setTempSeparations.toString(),
      actions.setTempSeparationValue.toString(),
      actions.addTempSeparation.toString(),
      actions.removeTempSeparation.toString()
    ),
    filter(({ payload: { doNotValidate } = {} }: ITempSeparationAction) => !doNotValidate),
    mergeMap(() => {
      const availableSieveSizes = state$.value.worksheetEditor.syncModel.data.Equipment.AllSieveSizes;
      const tempSeparations = state$.value.psdSieveAnalysis.separationManagement.separations;

      if (
        !availableSieveSizes ||
        availableSieveSizes.some((s: ISieveSize) => !s.Size || s.Size.uom !== availableSieveSizes[0].Size.uom)
      )
        throw new Error(
          'Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units. A PSD action may have been called for a non-PSD worksheet.'
        );

      const epicActionsOut: any[] = [];
      let precedingSeparationSieveSizeValue: number;
      tempSeparations.forEach((s: SeparationModel, i: number) => {
        const validSieveSizes = precedingSeparationSieveSizeValue
          ? availableSieveSizes.filter(
              (s: ISieveSize) => Number(s.Size!.value) < Number(precedingSeparationSieveSizeValue)
            )
          : availableSieveSizes;

        if (!validSieveSizes.some((o: ISieveSize) => o.Id === s.SeparatingSieveSizeId)) {
          epicActionsOut.push(
            actions.setTempSeparationValue({
              index: i,
              name: 'SeparatingSieveSizeId',
              value: null,
              doNotValidate: true,
            })
          );
        } else {
          precedingSeparationSieveSizeValue = availableSieveSizes.find(
            (a: ISieveSize) => a.Id === s.SeparatingSieveSizeId
          )?.Size.value;
        }
      });

      return of(...epicActionsOut);
    })
  );

export const applyTempSeparationsToWorksheet: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.applyTempSeparationsToWorksheet.toString()),
    switchMap(() =>
      callApiPutSeparations(
        state$.value.worksheetEditor.worksheetId,
        state$.value.psdSieveAnalysis.separationManagement.separations
      ).pipe(
        switchMap(() => syncAndAwaitResolve(action$, of(actions.applyTempSeparationsToWorksheetResolve()))),
        catchWithAuthErrors((err: any) =>
          of(actions.applyTempSeparationsToWorksheetReject(), addErrorNotice('Failed to apply separations data.', err))
        )
      )
    )
  );

/**
 * Removes a specific separation in one action without establishing "temp" separations
 * (for removing a specific separation with)
 */
export const removeSeparation: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.removeSeparation.toString()),
    mergeMap(({ payload: { id } }: RemoveSeparationAction) =>
      callApiGetSeparations(state$.value.worksheetEditor.worksheetId).pipe(
        switchMap(({ Separations: separations }: { Separations: SeparationModel[] }) => {
          if (!separations.some((s) => s.Id === id))
            throw new Error(`Cannot remove separation ${id}. Separation no longer exists. Please try reloading.`);

          return callApiPutSeparations(
            state$.value.worksheetEditor.worksheetId,
            separations.filter((s) => s.Id !== id)
          ).pipe(
            switchMap(() => syncAndAwaitResolve(action$, of(actions.removeSeparationResolve()))),
            catchWithAuthErrors((err: any) =>
              of(actions.removeSeparationReject(), addErrorNotice('Failed to remove separation.', err))
            )
          );
        }),
        catchWithAuthErrors((err: any) =>
          of(actions.removeSeparationReject(), addErrorNotice('Failed to remove separation.', err))
        )
      )
    )
  );

export const refreshSieves: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.refreshSieves.toString()),
    mergeMap((_) =>
      callApiRefreshSieves(state$.value.worksheetEditor.worksheetId).pipe(
        // TODO: The refresh sieve reponse does give us the current state of the worksheet,
        // but it is difficult to apply this without running a legitimate sync
        // so we just ignore it and trigger a sync.
        // Will likely revise this when the sync state is reorganised in v5.7.
        switchMap(() => syncAndAwaitResolve(action$, of(actions.refreshSievesResolve()))),
        catchWithAuthErrors((err: any) =>
          of(actions.refreshSievesReject(), addErrorNotice('Failed to refresh sieves.', err))
        )
      )
    )
  );
